import './ReactTabs.scss'
import React from 'react'

import {
  TabsV1IsEmptyFn,
  TabsV1Properties,
  TabsV1State,
  withAuthorPanelSwitch,
  withStandardBaseCssClass
} from '@adobe/aem-core-components-react-spa'
import { ComponentMapping, Container } from '@adobe/aem-react-editable-components'

import IsInEditorText from '../shared/IsInEditorText'

interface ScrollOptions {
  behavior?: 'smooth' | 'auto'
  block?: 'start' | 'center' | 'end' | 'nearest'
  offset?: number
}

interface ReactTabsProps extends TabsV1Properties {
  id?: string
  lob?: string
  tag?: any
  children?: React.ReactNode
  // New props for scroll behavior configuration
  scrollBehavior?: 'smooth' | 'auto'
  scrollOffset?: number
  disableAutoScroll?: boolean
}

class ReactTabs extends Container<ReactTabsProps, TabsV1State> {
  private hashChangeDebounceTimer: NodeJS.Timeout | null = null

  constructor(props: ReactTabsProps) {
    super(props)

    this.state = {
      activeIndex: !!props.activeItem && props.activeItem.length > 0 ? this.props.cqItemsOrder.indexOf(props.activeItem) : 0,
      componentMapping: this.props.componentMapping || ComponentMapping
    }

    this.handleTabNavClick = this.handleTabNavClick.bind(this)
    this.handleHashChange = this.handleHashChange.bind(this)
    this.handleAnchorClick = this.handleAnchorClick.bind(this)
  }

  componentDidMount(): void {
    // Handle initial hash on page load - process immediately without debouncing
    this.processHashChange()

    // Listen for hash changes
    window.addEventListener('hashchange', this.handleHashChange)
    // Listen for clicks on anchor links to prevent page reload
    document.addEventListener('click', this.handleAnchorClick)

    // Additional check after a short delay to ensure DOM is fully ready
    // This handles cases where the component mounts before all elements are rendered
    setTimeout(() => {
      this.processHashChange()
    }, 50)
  }

  componentWillUnmount(): void {
    // Clean up event listeners
    window.removeEventListener('hashchange', this.handleHashChange)
    document.removeEventListener('click', this.handleAnchorClick)

    // Clean up debounce timer
    if (this.hashChangeDebounceTimer) {
      clearTimeout(this.hashChangeDebounceTimer)
      this.hashChangeDebounceTimer = null
    }
  }

  componentDidUpdate(prevProps: Readonly<TabsV1Properties>): void {
    if (
      this.props.activeIndexFromAuthorPanel !== undefined &&
      prevProps.activeIndexFromAuthorPanel != this.props.activeIndexFromAuthorPanel
    ) {
      this.setState({ activeIndex: this.props.activeIndexFromAuthorPanel })
    }
  }

  /**
   * Calculate scroll offset to account for fixed headers
   * @returns {number} The offset in pixels
   */
  private calculateScrollOffset(): number {
    const { scrollOffset } = this.props

    // Use provided offset if available
    if (scrollOffset !== undefined) {
      return scrollOffset
    }

    // Default offset calculation for common fixed header scenarios
    let offset = 0

    // Check for common fixed header selectors
    const fixedHeaderSelectors = [
      '.fixed-header',
      '.sticky-header',
      'header[style*="position: fixed"]',
      'nav[style*="position: fixed"]',
      '.navbar-fixed-top'
    ]

    fixedHeaderSelectors.forEach((selector) => {
      const element = document.querySelector(selector)
      if (element) {
        const rect = element.getBoundingClientRect()
        offset = Math.max(offset, rect.height)
      }
    })

    // Add some padding for better UX
    return offset + 20
  }

  /**
   * Scroll to the tabs component with smooth behavior
   * @param {ScrollOptions} options - Scroll configuration options
   */
  private scrollToComponent(options?: ScrollOptions): void {
    const { disableAutoScroll = false, scrollBehavior = 'smooth' } = this.props

    // Don't scroll if disabled
    if (disableAutoScroll) {
      return
    }

    // Get the component element
    const componentElement = document.getElementById(this.props.id || '')

    // Check if element exists before proceeding
    if (!componentElement) {
      console.log('ReactTabs: Component element not found for scrolling', this.props.id)
      return
    }

    try {
      const scrollOptions: ScrollIntoViewOptions = {
        behavior: options?.behavior || scrollBehavior,
        block: options?.block || 'start',
        inline: 'nearest'
      }

      // Calculate offset for fixed headers
      const offset = this.calculateScrollOffset()

      if (offset > 0) {
        // If we have an offset, we need to scroll manually to account for it
        const elementRect = componentElement.getBoundingClientRect()
        const currentScrollY = window.scrollY || window.pageYOffset || 0
        const targetPosition = currentScrollY + elementRect.top - offset

        if (scrollOptions.behavior === 'smooth') {
          window.scrollTo({
            top: targetPosition,
            behavior: 'smooth'
          })
        } else {
          window.scrollTo(0, targetPosition)
        }
      } else {
        // No offset needed, use native scrollIntoView
        componentElement.scrollIntoView(scrollOptions)
      }
    } catch (error) {
      // Fallback for browsers that don't support scrollIntoView options
      console.log('ReactTabs: Smooth scrolling not supported, falling back to basic scroll', error)
      try {
        componentElement.scrollIntoView()
      } catch (fallbackError) {
        console.log('ReactTabs: Failed to scroll to component', fallbackError)
      }
    }
  }

  handleTabNavClick(index: number) {
    if (this.state.activeIndex !== index) {
      this.setState({
        activeIndex: index
      })
    }
  }

  handleHashChange() {
    // Debounce rapid hash changes to improve performance
    if (this.hashChangeDebounceTimer) {
      clearTimeout(this.hashChangeDebounceTimer)
    }

    this.hashChangeDebounceTimer = setTimeout(() => {
      this.processHashChange()
    }, 100) // 100ms debounce delay
  }

  /**
   * Process hash change with error handling and automatic scrolling
   * @private
   */
  private processHashChange(): void {
    try {
      const hash = window.location.hash.substring(1)
      if (!hash) return

      const sanitizedHash = hash.replace(/[^a-zA-Z0-9-_]/g, '')

      // Support both simple and component-prefixed tabpanel formats
      const { id: componentId } = this.props
      const tabIndex = this.props.cqItemsOrder.findIndex((item, index) => {
        const simpleFormat = `tabpanel-${index}`
        const prefixedFormat = componentId ? `${componentId}-tabpanel-${index}` : null

        return sanitizedHash === simpleFormat || (prefixedFormat && sanitizedHash === prefixedFormat)
      })

      if (tabIndex !== undefined && tabIndex >= 0) {
        if (tabIndex !== this.state.activeIndex) {
          this.setState({ activeIndex: tabIndex })

          // Scroll to component after state update
          setTimeout(() => {
            this.scrollToComponentWithErrorHandling()
          }, 100)
        }
      } else {
        // Hash doesn't match any tab - log for debugging but don't throw error
        console.log(`ReactTabs: No tab found for hash "${sanitizedHash}", ignoring navigation`)
      }
    } catch (error) {
      // Graceful error handling - log error but don't break functionality
      console.log('ReactTabs: Error processing hash change:', error)
    }
  }

  /**
   * Scroll to component with comprehensive error handling and graceful degradation
   * @private
   */
  private scrollToComponentWithErrorHandling(): void {
    try {
      this.scrollToComponent()
    } catch (scrollError) {
      // If scrolling fails, log the error but continue with tab activation
      console.log('ReactTabs: Failed to scroll to component after hash navigation:', scrollError)

      // Attempt basic fallback scrolling without smooth behavior
      try {
        const componentElement = document.getElementById(this.props.id || '')
        if (componentElement) {
          componentElement.scrollIntoView()
        }
      } catch (fallbackError) {
        // Even fallback failed - log but don't throw
        console.log('ReactTabs: All scroll attempts failed:', fallbackError)
      }
    }
  }

  handleAnchorClick(event: Event) {
    const target = event.target as HTMLElement

    // Check if clicked element is an anchor link
    if (target.tagName === 'A' || target.closest('a')) {
      const anchor = target.tagName === 'A' ? (target as HTMLAnchorElement) : (target.closest('a') as HTMLAnchorElement)
      const href = anchor.getAttribute('href')

      // Only handle hash links that start with #
      if (href && href.startsWith('#')) {
        const hash = href.substring(1)
        const sanitizedHash = hash.replace(/[^a-zA-Z0-9-_]/g, '')

        // Support both simple and component-prefixed tabpanel formats
        const { id: componentId } = this.props
        const tabIndex = this.props.cqItemsOrder.findIndex((item, index) => {
          const simpleFormat = `tabpanel-${index}`
          const prefixedFormat = componentId ? `${componentId}-tabpanel-${index}` : null

          return sanitizedHash === simpleFormat || (prefixedFormat && sanitizedHash === prefixedFormat)
        })

        if (tabIndex >= 0) {
          // Prevent page reload
          event.preventDefault()

          // Update URL hash without page reload
          if (window.history && window.history.pushState) {
            window.history.pushState(null, '', `#${sanitizedHash}`)
          } else {
            window.location.hash = sanitizedHash
          }

          // Activate the tab and scroll to component
          this.setState({ activeIndex: tabIndex }, () => {
            // Use requestAnimationFrame to ensure DOM has updated before scrolling
            requestAnimationFrame(() => {
              this.scrollToComponentWithErrorHandling()
            })
          })
        }
      }
    }
  }

  getTabId(index: number): string {
    return `tabpanel-${index}`
  }

  get effectiveChildComponents(): JSX.Element[] {
    // If we have direct children passed via props (from tests), use those
    if (this.props.children) {
      return React.Children.toArray(this.props.children) as JSX.Element[]
    }
    // Otherwise, use the Container's childComponents getter
    return this.childComponents
  }

  tabNavigation() {
    const childComponents: JSX.Element[] = []
    const { isInEditor = false, lob = '', tag = 'h4' } = this.props
    const ElementTag = tag

    if (!this.props.cqItems || !this.props.cqItemsOrder) {
      return childComponents
    }

    return (
      <>
        {IsInEditorText(isInEditor, 'React Tabs')}
        <div className={`react-tabs-container container ${lob}`}>
          <ul role='tablist'>
            {this.props.cqItemsOrder.map((item, index) => {
              const tab = this.props.cqItems[item]
              const tabId = this.getTabId(index)
              return (
                <li
                  key={`${tab['cq:panelTitle']}-${index}`}
                  tabIndex={this.state.activeIndex === index ? 0 : -1}
                  role='tab'
                  aria-controls={tabId}
                  aria-selected={this.state.activeIndex === index}
                  onClick={() =>
                    this.setState({
                      activeIndex: index
                    })
                  }
                  onKeyDown={(e) =>
                    e.key === 'Enter'
                      ? this.setState({
                          activeIndex: index
                        })
                      : null
                  }
                >
                  <ElementTag className={`tab-title ${this.state.activeIndex === index ? 'active' : ''}`}>
                    {tab['cq:panelTitle']}
                  </ElementTag>
                  <div className={`${this.state.activeIndex === index ? 'active-tab-seperator' : 'no-show'}`}></div>
                </li>
              )
            })}
          </ul>
          <div className='tab-content'>
            {isInEditor === true ? (
              <>
                {this.props.cqItemsOrder.map((item, index) => {
                  const isVisible = this.state.activeIndex === index
                  const styles = { display: !isVisible ? 'none' : 'block' }
                  const tabId = this.getTabId(index)
                  const childComponent = this.effectiveChildComponents[index]

                  return (
                    <div
                      style={styles}
                      role='tabpanel'
                      id={tabId}
                      key={'tab-content-' + (item || index)}
                      aria-hidden={!isVisible}
                    >
                      {childComponent || null}
                    </div>
                  )
                })}
              </>
            ) : (
              <>
                {this.props.cqItemsOrder.map((item, index) => {
                  const isVisible = this.state.activeIndex === index
                  const styles = { display: !isVisible ? 'none' : 'block' }
                  const tabId = this.getTabId(index)
                  const childComponent = this.effectiveChildComponents[index]

                  return (
                    <div style={styles} aria-hidden={!isVisible} id={tabId} role='tabpanel' key={'tab-content-' + index}>
                      {childComponent || null}
                    </div>
                  )
                })}
              </>
            )}
          </div>
        </div>
      </>
    )
  }

  get tabContainerProps() {
    const attrs = this.containerProps
    attrs['className'] = attrs.className + ' ' + this.props.baseCssClass
    attrs['data-cmp-is'] = 'tabs'
    attrs['data-panelcontainer'] = 'tabs'
    attrs['data-testid'] = 'reacttabs-container'

    return attrs
  }

  render() {
    const isEmpty = TabsV1IsEmptyFn(this.props)
    const { id = '' } = this.props
    return (
      <div {...this.tabContainerProps} id={id}>
        {!isEmpty && this.tabNavigation()}
        {isEmpty &&
          this.props.cqItems &&
          this.props.cqItemsOrder &&
          this.props.cqItemsOrder.length > 0 &&
          this.tabNavigation()}
        {this.placeholderComponent}
      </div>
    )
  }
}

export default withStandardBaseCssClass(withAuthorPanelSwitch(ReactTabs), 'cmp-tabs')
